// Ports the pane view-model depends on. Implementations: demo (in-memory, dev only) and the real
// offline-first workspace (IndexedDB + outbox + sync, P04/P05). Keeps UI code free of storage/transport details.
import type { FileRef, Pane, Project, User, Desks } from '@/domain/model';
import type { Connectivity } from '@/app/ports';
import type { MarkdownApi } from '@/modules/markdown/api';
import type { DraftStore } from '@/modules/markdown/draftStore';

export interface Result { ok: boolean; error?: string; notice?: string }

// ── Online-only sharing/admin contracts (design v6 F4, F5, F7–F9, F3; docs/sharing-links.md, docs/project-trash.md,
// docs/auth-usernames.md). Implementations throw TransportError (kind + server error code) on failure.
export interface PersonRef { id: string; name: string; username?: string | null; color?: string | null; photo_url?: string | null }
export interface InvitationView { id: string; email: string | null; email_hint?: string | null; invitee: PersonRef | null; role: string; status?: string; invited_by: PersonRef | null; created_at: string; expires_at?: string }
export interface SharingInfo { members: Array<PersonRef & { role: string }>; invitations: InvitationView[]; can_invite: boolean; can_cancel: boolean }
export interface InboxInvite { id: string; workspace_id: string; project: { id: string; name: string; open_task_count: number; member_count: number }; inviter: PersonRef | null; role: string; created_at: string; expires_at?: string }
export interface ShareLink { id: string; scope: 'project' | 'list' | 'tag'; list_id: string | null; list_name: string | null; list_available: boolean; tag: string | null; url: string; created_by: string; created_at: string; last_viewed_at?: string | null }
export interface TrashedProject { id: string; name: string; revision: number; deleted_at: string; deleted_by: PersonRef | null; restore_until?: string; days_left: number; list_count: number; task_count: number }
export interface OnlinePort {
  sharing(projectId: string): Promise<SharingInfo>;
  invite(projectId: string, invitee: string, role?: string): Promise<{ already_invited: boolean; invitation?: InvitationView }>;
  cancelInvite(projectId: string, invitationId: string): Promise<void>;
  links(projectId: string): Promise<ShareLink[]>;
  createLink(projectId: string, scope: { scope: 'project' } | { scope: 'list'; list_id: string } | { scope: 'tag'; tag: string }): Promise<ShareLink>;
  revokeLink(projectId: string, linkId: string): Promise<void>;
  inbox(): Promise<InboxInvite[]>;
  acceptInvite(id: string): Promise<{ workspace_id: string; project_id: string; role: string; path: string }>;
  declineInvite(id: string): Promise<void>;
  trashedProjects(): Promise<TrashedProject[]>;
  purgeProject(projectId: string): Promise<void>;
  updateProfile(patch: { username?: string; display_name?: string }): Promise<{ id: string; username: string | null; display_name: string }>;
  /** v7: replaces the profile photo (square JPEG/PNG/WebP ≤ 512 KiB); resolves to the new photo URL. */
  uploadAvatar(body: Blob): Promise<string>;
  removeAvatar(): Promise<void>;
}

export interface WorkspacePort {
  /** Persist the change from `before` to `after` (atomic local write + outbox ops). Returns an undo handle. */
  commit(before: Project[], after: Project[], actorId: string, meta?: { activityGroup?: string }): unknown;
  /** Apply the inverse of the given handles conditionally on the current state; returns the new projects. */
  undo(current: Project[], handles: unknown[], actorId: string): Project[];
  /** v10: `desks` = every desktop (the current one's panes are `panes`). */
  saveLayout(panes: Pane[], desks?: Desks): void;
  saveProfile(patch: { name?: string; email?: string }): void;
  changePassword(current: string, next: string): Promise<Result>;
  inviteToProject(projectId: string, email: string): Promise<Result & { user?: User }>;
  removeProjectMember(projectId: string, userId: string): Promise<Result>;
  attachFile(taskId: string, file: File, opts?: { role?: 'note_image' | 'comment_image' | 'project_comment_image'; ar?: number }): Promise<FileRef | null>;
  /** Removes an attachment (queued while offline). Optional: the demo workspace removes in memory only. */
  removeFile?(taskId: string, fileId: string): Promise<void>;
  accounts(): User[];
  switchAccount(userId: string): void;
  signOut(): void;
  newId(): string;
  /** true only for the in-memory demo workspace */
  demo?: boolean;
  /** online-only REST (sharing, invites, links, project trash, profile); absent in the demo workspace */
  api?: OnlinePort;
  /** the home workspace id (your own: personal layout, new projects) */
  workspaceId?: string;
  /** Queues a project command with no local diff base (restore of a project this device no longer holds). */
  projectCommand?(projectId: string, command: 'deleteProject' | 'restoreProject', baseRevision: number | null): Promise<void>;
  /** Runs a sync cycle now (after an invite is accepted); resolves when it finished. */
  syncNow?(): Promise<void>;
  /** Keeps a profile change made through `api.updateProfile` across projection reloads. */
  rememberProfile?(patch: { username?: string; photo?: string | null }): void;
  /** Navigates to another workspace of this account (full reload into its local partition). */
  openWorkspace?(path: string): void;
  /** Workspaces of this account (from /auth/me at sign-in). */
  workspaces?: Array<{ id: string; name: string; role: string; owner_name?: string | null }>;
  /** True when that workspace's projects are loaded in this view (all workspaces are shown together). */
  hasWorkspace?(workspaceId: string): boolean;
  /** Editable Markdown attachments (design v6 F14–F16): content API, local draft store, reconnect signal. */
  markdown?: { api: MarkdownApi; store: DraftStore; connectivity?: Connectivity };
}

/** Small non-sensitive UI preferences (theme, density…). Backed by localStorage, server sync in P15. */
export interface PrefStore {
  get(key: string): string | null;
  set(key: string, value: string): void;
}

export const localPrefs: PrefStore = {
  get(key) { try { return localStorage.getItem(key); } catch { return null; } },
  set(key, value) { try { if (value === '') localStorage.removeItem(key); else localStorage.setItem(key, value); } catch { /* storage unavailable */ } },
};
